import React, { useState } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { Button, Row, Col, Collapse, Input, Label } from 'reactstrap';
import domainModel, { article as articleModel } from 'domain-model';

import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import BlurableNumberInput from './BlurableNumberInput';
import {
  calculateFromSetQuantity,
  getQuantityFromCollis,
  calculateQuantityOnIncrement,
} from './articleQuantityInputCalculatorsLegacy';

function getQuantityIncrement(articleViewModel, orderUnit) {
  switch (orderUnit) {
    case 'single':
      return 1;
    case 'package':
      return articleViewModel.colliSize;
    case 'weight':
      return articleViewModel.minWeightIncrement;
    default:
      return 1;
  }
}

function ArticleQuantityInputLegacy({
  name,
  value,
  article,
  onChange,
  disabled,
  shopConfig,
  possibleToSetAnyValue,
  cartRow,
  showOrderUnitSelect,
}) {
  const isPrivab = domainModel.shopConfig.isPrivabShop(shopConfig);
  const legacyFlagShowOrderUnitSelect = showOrderUnitSelect;
  if (!article) return <></>;

  // the sku can be passed directly as a prop, or it can be fetched from the cartRow

  const articleViewModel = articleModel.getViewModel(article);
  const { colliSize, minWeightIncrement } = articleViewModel;

  // Remove this useState when SKU is used is in shop
  const [orderUnit, setOrderUnit] = useState(
    articleViewModel.orderUnit || 'single',
  );

  const quantityToDisplay = _.includes(['single', 'weight'], orderUnit)
    ? value
    : value / colliSize;

  const availableAsSingleAndPackage =
    colliSize > 1 && articleViewModel.orderUnit === 'single';

  const [isEditing, setIsEditing] = useState(false);

  const isOrderUnitSelectOpen =
    availableAsSingleAndPackage && quantityToDisplay > 0;

  const quantityIncrement = getQuantityIncrement(article, orderUnit);

  const switchId = `${cartRow?.id || ''}${article.id}-orderUnitSwitch`;
  const hidePlusMinus = disabled && isEditing;

  const changeOrderUnit = () => {
    const newOrderUnit = orderUnit === 'single' ? 'package' : 'single';
    setOrderUnit(newOrderUnit);
    if (newOrderUnit === 'package') {
      const newValue = getQuantityFromCollis(value, colliSize);
      if (newValue !== value) {
        onChange(name, newValue);
      }
    }
  };

  function quantityInOffToggledUnit() {
    if (orderUnit === 'single') {
      const rest = value % colliSize;
      const packs = (value - rest) / colliSize;
      if (rest === 0) {
        return `${packs} frp`;
      }
      return `${packs} frp + ${rest} st`;
    }
    return `${value} st`;
  }

  return (
    <>
      <Row
        className={`justify-content-between align-items-center g-0 ${
          disabled ? ' bg-light text-muted' : 'text-end bg-white'
        }`}
      >
        {quantityToDisplay === 0 && isPrivab && (
          <Col xs={12}>
            <Button
              className="w-100 buyButton"
              onClick={async () => {
                await onChange(
                  name,
                  calculateQuantityOnIncrement(
                    value,
                    quantityIncrement,
                    orderUnit,
                    minWeightIncrement,
                  ),
                );
              }}
            >
              Köp
            </Button>
          </Col>
        )}
        {((quantityToDisplay > 0 && isPrivab) || !isPrivab) && (
          <>
            {!isEditing && (
              <Col className={`p-0 ${hidePlusMinus ? 'd-none' : ''}`} xs={3}>
                <Button
                  className="d-inline-block rounded-0 shadow-none w-100 subtractButton"
                  id={`subtractButton-${article.id}`}
                  color={quantityToDisplay <= 0 ? 'muted' : 'secondary'}
                  onClick={() => {
                    onChange(
                      name,
                      calculateQuantityOnIncrement(
                        value,
                        -1 * quantityIncrement,
                        orderUnit,
                        minWeightIncrement,
                      ),
                    );
                  }}
                  disabled={disabled || quantityToDisplay <= 0}
                >
                  <FontAwesomeIcon icon={faMinus} />
                </Button>
              </Col>
            )}
            <Col
              className="text-center py-1 articleQuantityInputLegacy"
              xs={isEditing ? 12 : 6}
            >
              <BlurableNumberInput
                styles={{ minHeight: '35px' }}
                className="d-inline-block "
                value={quantityToDisplay}
                onChange={(propName, numberValue) => {
                  setIsEditing(false);
                  onChange(
                    name,
                    calculateFromSetQuantity(
                      numberValue,
                      orderUnit,
                      colliSize,
                      possibleToSetAnyValue ? 0.01 : minWeightIncrement, // this is to allow the warehouse to have full control of the deliveed value
                    ),
                  );
                }}
                name={name}
                disabled={disabled}
                step={quantityIncrement}
                onEditBegin={() => setIsEditing(true)}
                onEditEnd={() => setIsEditing(false)}
              >
                {quantityToDisplay}{' '}
                {orderUnit === 'package'
                  ? ' frp'
                  : ` ${articleViewModel.displayOrderableUnit}`}
              </BlurableNumberInput>
            </Col>
            {!isEditing && (
              <Col className={`p-0 ${hidePlusMinus ? 'd-none' : ''}`} xs={3}>
                <Button
                  className="d-inline-block w-100 rounded-0 shadow-none addButton"
                  id={`addButton-${article.id}`}
                  color="primary"
                  disabled={disabled}
                  onClick={async () => {
                    await onChange(
                      name,
                      calculateQuantityOnIncrement(
                        value,
                        quantityIncrement,
                        orderUnit,
                        minWeightIncrement,
                      ),
                    );
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} />
                </Button>
              </Col>
            )}
          </>
        )}
      </Row>
      {!disabled && legacyFlagShowOrderUnitSelect && (
        <Row
          className={`align-items-center g-0 ${
            disabled ? ' bg-light text-muted' : 'text-end bg-white'
          }`}
        >
          <Collapse isOpen={isOrderUnitSelectOpen}>
            <Row className="mt-1 g-0">
              <Col xs={6} className="text-center text-sm px-0 mt-1">
                {quantityInOffToggledUnit()}
              </Col>
              <Col xs={5} className="text-center px-0 mt-1 pr-1">
                <Input
                  id={switchId}
                  checked={orderUnit === 'package'}
                  type="checkbox"
                  data-switch="bool"
                  onChange={changeOrderUnit}
                />
                <Label
                  for={switchId}
                  className="bg-primary"
                  data-on-label="FRP"
                  data-off-label="ST"
                />
              </Col>
            </Row>
          </Collapse>
        </Row>
      )}
    </>
  );
}

ArticleQuantityInputLegacy.propTypes = {
  name: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.number,
  article: PropTypes.object,
  disabled: PropTypes.bool,
  possibleToSetAnyValue: PropTypes.bool,
  showOrderUnitSelect: PropTypes.bool,
  cartRow: PropTypes.object,
  shopConfig: PropTypes.object,
};

ArticleQuantityInputLegacy.defaultProps = {
  article: null,
  cartRow: null,
  name: null,
  disabled: false,
  showOrderUnitSelect: false,
  value: 0,
  possibleToSetAnyValue: false,
  shopConfig: {},
};

export default ArticleQuantityInputLegacy;
